import axios from 'axios';
import { getValidToken } from './authUtils';
import { handleSessionExpired } from './common';
import {
    OFFLINE_ERROR_MESSAGE,
    NETWORK_ERROR_MESSAGE,
    isRetryableError,
    getRetryDelay,
    getMaxRetries,
} from './networkUtils';
import { showToast } from '../services/toast';
import { addObservabilityInterceptors } from '../observability';

const instance = axios.create({
    maxRedirects: 5,
    validateStatus: function (status) {
        return status >= 200 && status < 300;
    }
});

addObservabilityInterceptors(instance);

/** Paths that do not require a session token (public APIs). */
const PUBLIC_PATH_SUFFIXES = [
    '/student-login/studentLogin',
    '/student-login/LandingPageMedia',
    '/organization/branding',
];

function hasUsableRequestUrl(config) {
    const url = config?.url;
    return typeof url === 'string' && url.trim() !== '' && url.trim() !== 'undefined';
}

function createRequestError(message, config, extra = {}) {
    const error = new Error(message);
    error.config = config;
    Object.assign(error, extra);
    return error;
}

function isProtectedRequest(url) {
    if (!url || typeof url !== 'string') return true;
    const path = url.replace(/^https?:\/\/[^/]+/, '');
    return !PUBLIC_PATH_SUFFIXES.some((suffix) => path.includes(suffix));
}

function isOnLoginPage() {
    try {
        const path = typeof window !== 'undefined' && window.location?.pathname;
        return path != null && path.replace(/\/$/, '') === '/login';
    } catch {
        return false;
    }
}

instance.interceptors.request.use(
    (config) => {
        config.headers = config.headers ?? {};

        if (!hasUsableRequestUrl(config)) {
            return Promise.reject(createRequestError('Missing request URL', config, {
                isConfigError: true,
                skipRetry: true,
                skipNetworkToast: true,
            }));
        }

        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return Promise.reject(createRequestError(OFFLINE_ERROR_MESSAGE, config, {
                isOffline: true,
                skipRetry: true,
            }));
        }

        const token = getValidToken(); // Only non-expired token; never send expired.

        if (isProtectedRequest(config.url)) {
            if (!token) {
                if (!isOnLoginPage()) {
                    handleSessionExpired();
                }
                return Promise.reject(createRequestError('No valid token', config, {
                    isAuthError: true,
                    skipRetry: true,
                    skipNetworkToast: true,
                }));
            }
            config.headers.sessionToken = token;
            return config;
        }

        if (token) {
            config.headers.sessionToken = token;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

instance.interceptors.response.use(
    (response) => response,
    // (response) => {
    //     console.log('API success:', response.config?.url, response.status, response.data);
    //     return response;
    // },
    (error) => {
        if (error.response?.status === 401) {
            if (!isOnLoginPage()) {
                handleSessionExpired();
            }
            return Promise.reject(error);
        }

        const config = error.config || {};
        const retryCount = config.__retryCount ?? 0;
        if (!error.skipRetry && hasUsableRequestUrl(config) && isRetryableError(error) && retryCount < getMaxRetries()) {
            config.__retryCount = retryCount + 1;
            const delay = getRetryDelay(config.__retryCount);
            return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
                instance.request(config)
            );
        }

        if (error.isOffline) {
            showToast(OFFLINE_ERROR_MESSAGE, 'warn');
        } else if (!error.response && !error.skipNetworkToast) {
            showToast(NETWORK_ERROR_MESSAGE, 'error');
        }

        return Promise.reject(error);
    }
);

export const apiClient = instance;
